import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Card, Form, Spinner} from 'react-bootstrap';
import {Formik} from 'formik';
import * as api from '../api';
import GoogleSignIn from './google-sign-in';
import AppleSignIn from './apple-sign-in';
import {FaApple, FaGoogle, FaKey, FaUnlink} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';

const AuthMethods = ({token}) => {
  const {t} = useTranslation();
  const [methods, setMethods] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appleLinkPassword, setAppleLinkPassword] = useState('');

  const refresh = useCallback(async () => {
    const result = await api.getAuthMethods(token);
    if (result.status === 200) setMethods(result.json);
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const linkGoogle = useCallback(async credential => {
    setLoading(true);
    const result = await api.linkGoogle(credential, token);
    setLoading(false);
    if (result.status === 200) {
      setNotice({variant: 'success', text: t('AUTH_GOOGLE_LINKED')});
      refresh();
    } else {
      setNotice({variant: 'danger', text: result.json.error === 'google_email_mismatch'
        ? t('AUTH_GOOGLE_EMAIL_MISMATCH')
        : t('AUTH_LINK_FAILED', {error: result.json.error || result.status})});
    }
  }, [refresh, t, token]);

  const unlinkGoogle = async () => {
    if (!window.confirm(t('AUTH_UNLINK_GOOGLE_CONFIRM'))) return;
    setLoading(true);
    const result = await api.unlinkGoogle(token);
    setLoading(false);
    if (result.status === 200) {
      setNotice({variant: 'success', text: t('AUTH_GOOGLE_UNLINKED')});
      refresh();
    } else {
      setNotice({variant: 'danger', text: t('AUTH_PASSWORD_REQUIRED_UNLINK_GOOGLE')});
    }
  };

  const linkApple = useCallback(async authorization => {
    setLoading(true);
    const result = await api.linkApple({...authorization, password: appleLinkPassword}, token);
    setLoading(false);
    if (result.status === 200) {
      setNotice({variant: 'success', text: t('AUTH_APPLE_LINKED')});
      setAppleLinkPassword('');
      refresh();
    } else {
      setNotice({variant: 'danger', text: result.json.error === 'apple_email_mismatch_reauthentication_required'
        ? t('AUTH_APPLE_PRIVATE_EMAIL_HELP')
        : t('AUTH_APPLE_LINK_FAILED', {error: result.json.error || result.status})});
    }
  }, [appleLinkPassword, refresh, t, token]);

  const unlinkApple = async () => {
    if (!window.confirm(t('AUTH_UNLINK_APPLE_CONFIRM'))) return;
    setLoading(true);
    const result = await api.unlinkApple(token);
    setLoading(false);
    if (result.status === 200) {
      setNotice({variant: 'success', text: t('AUTH_APPLE_UNLINKED')});
      refresh();
    } else {
      setNotice({variant: 'danger', text: t('AUTH_PASSWORD_REQUIRED_UNLINK_APPLE')});
    }
  };

  if (!methods) return <Spinner animation="border" size="sm"/>;

  return (
    <Card className="mt-4 mb-4 text-white" style={{backgroundColor: 'transparent', borderColor: '#00c8d4'}}>
      <Card.Body>
        <Card.Title>{t('AUTH_METHODS_TITLE')}</Card.Title>
        {notice ? <Alert variant={notice.variant}>{notice.text}</Alert> : null}
        <p>{t('AUTH_REPLAYER_PASSWORD')}: <strong>{methods.hasPassword ? t('AUTH_CONFIGURED') : t('AUTH_NOT_CONFIGURED')}</strong></p>
        <p>Google: <strong>{methods.googleLinked ? t('AUTH_LINKED') : t('AUTH_NOT_LINKED')}</strong></p>
        {!methods.googleLinked ? <GoogleSignIn className="profile-google-connect" onCredential={linkGoogle}/> : null}
        {methods.googleLinked ? (
          <Button className="profile-auth-action profile-auth-action--disconnect" disabled={loading || !methods.hasPassword} onClick={unlinkGoogle}>
            <FaGoogle aria-hidden="true"/><FaUnlink aria-hidden="true"/>{t('AUTH_UNLINK_GOOGLE')}
          </Button>
        ) : null}
        {methods.googleLinked && !methods.hasPassword ? (
          <Alert variant="info" className="mt-3">
            {t('AUTH_SET_PASSWORD_BEFORE_UNLINK_GOOGLE')}
          </Alert>
        ) : null}
        <hr style={{borderColor: 'rgba(255,255,255,.35)'}}/>
        <p>Apple: <strong>{methods.appleLinked ? t('AUTH_LINKED') : t('AUTH_NOT_LINKED')}</strong></p>
        {!methods.appleLinked && methods.hasPassword ? (
          <Form.Group>
            <Form.Label>{t('AUTH_REPLAYER_PASSWORD')} <small>{t('AUTH_APPLE_PASSWORD_HELP')}</small></Form.Label>
            <Form.Control type="password" value={appleLinkPassword} onChange={event => setAppleLinkPassword(event.target.value)}/>
          </Form.Group>
        ) : null}
        {!methods.appleLinked ? <AppleSignIn className="profile-auth-action profile-auth-action--apple" label={t('AUTH_LINK_APPLE')} onAuthorization={linkApple}/> : null}
        {methods.appleLinked ? (
          <Button className="profile-auth-action profile-auth-action--disconnect" disabled={loading || !methods.hasPassword} onClick={unlinkApple}>
            <FaApple aria-hidden="true"/><FaUnlink aria-hidden="true"/>{t('AUTH_UNLINK_APPLE')}
          </Button>
        ) : null}
        {methods.appleLinked && !methods.hasPassword ? (
          <Alert variant="info" className="mt-3">{t('AUTH_SET_PASSWORD_BEFORE_UNLINK_APPLE')}</Alert>
        ) : null}
        {!methods.hasPassword ? (
          <Formik initialValues={{password: '', passwordConfirm: ''}} onSubmit={async (values, helpers) => {
            setLoading(true);
            const result = await api.setLoginPassword(values, token);
            setLoading(false);
            if (result.status === 200) {
              helpers.resetForm();
              setNotice({variant: 'success', text: t('AUTH_PASSWORD_CONFIGURED')});
              refresh();
            } else {
              setNotice({variant: 'danger', text: result.json.error === 'password_not_matched' ? t('AUTH_PASSWORDS_MISMATCH') : t('AUTH_ERROR', {error: result.json.error})});
            }
          }}>
            {({handleSubmit, handleChange, values}) => (
              <Form onSubmit={handleSubmit} className="mt-3 text-white">
                <Form.Group>
                  <Form.Label>{t('AUTH_NEW_PASSWORD')}</Form.Label>
                  <Form.Control required type="password" name="password" value={values.password} onChange={handleChange}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('AUTH_CONFIRM_PASSWORD')}</Form.Label>
                  <Form.Control required type="password" name="passwordConfirm" value={values.passwordConfirm} onChange={handleChange}/>
                </Form.Group>
                <Button type="submit" className="profile-auth-action profile-auth-action--save" disabled={loading}><FaKey aria-hidden="true"/>{t('AUTH_SET_PASSWORD')}</Button>
              </Form>
            )}
          </Formik>
        ) : null}
      </Card.Body>
    </Card>
  );
};

AuthMethods.propTypes = {token: PropTypes.string.isRequired};

export default AuthMethods;
